import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setSubmitted(true);
      setFormError('');
      return;
    }

    if (!email.trim()) {
      setFormError('Please enter your email');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d973p5ltfb6ioa7s1ukg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim() }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse errors */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubmitted(true);
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-foreground-950 rounded-t-3xl mx-3 md:mx-5 mt-10">
      <div className="w-full px-6 md:px-10 lg:px-16 pt-16 pb-10 max-w-[1280px] mx-auto">
        <div className="flex flex-col md:flex-row items-start justify-between mb-12 gap-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl font-bold text-white tracking-tight">
              Kakobuy<span className="text-primary-400">Finds</span>
            </span>
          </Link>
          <p className="text-foreground-400 text-sm max-w-sm">
            Your trusted resource for Kakobuy Spreadsheet guides, product discoveries, and shopping resources.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Categories</h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(0, 4).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">More Categories</h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(4, 8).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Guides</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/articles/what-is-kakobuy-spreadsheet" className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap">
                What Is Kakobuy Spreadsheet
              </Link>
              <Link to="/articles/kakobuy-spreadsheet-tips" className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap">
                Expert Tips
              </Link>
              <Link to="/articles/kakobuy-spreadsheet-vs-alternatives" className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap">
                Comparison Guide
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-foreground-400 text-sm hover:text-white transition-colors whitespace-nowrap"
              >
                Kakobuy Sheet
              </a>
            </div>
          </div>

          <div className="md:text-right">
            <p className="font-serif text-white/70 text-sm mb-1">
              &copy; {new Date().getFullYear()} KakobuyFinds
            </p>
            <p className="font-serif text-foreground-500 text-xs mb-4">All rights reserved</p>
            <div className="flex items-center gap-3 md:justify-end">
              <a href="#" className="text-foreground-400 hover:text-white transition-colors underline cursor-pointer" aria-label="Twitter">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-twitter-x-line"></i>
                </span>
              </a>
              <a href="#" className="text-foreground-400 hover:text-white transition-colors underline cursor-pointer" aria-label="Reddit">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-reddit-line"></i>
                </span>
              </a>
              <a href="#" className="text-foreground-400 hover:text-white transition-colors underline cursor-pointer" aria-label="Pinterest">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-pinterest-line"></i>
                </span>
              </a>
              <a href="#" className="text-foreground-400 hover:text-white transition-colors underline cursor-pointer" aria-label="YouTube">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-youtube-line"></i>
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-800 pt-10">
          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="w-full lg:w-1/3">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20reading%20nook%20with%20fashion%20magazines%20and%20open%20laptop%20on%20soft%20velvet%20armchair%20warm%20amber%20lamp%20light%20casting%20gentle%20shadows%20hygge%20aesthetic%20with%20plush%20textures%20and%20intimate%20atmosphere%20dark%20academia%20meets%20modern%20comfort%20deep%20burgundy%20and%20warm%20gold%20tones&width=400&height=280&seq=footer-lifestyle-v2&orientation=landscape"
                alt="Kakobuy Finds lifestyle"
                className="w-full h-48 object-cover object-top rounded-xl"
              />
            </div>

            <div className="w-full lg:w-1/3">
              <p className="font-serif text-2xl md:text-3xl text-white/80 leading-relaxed">
                Discover more.
                <br />
                Shop smarter.
                <br />
                Save time.
              </p>
            </div>

            <div className="w-full lg:w-1/3">
              <p className="text-foreground-400 text-sm mb-1">Stay updated with the latest finds</p>
              <p className="text-foreground-500 text-xs mb-3">Get weekly Kakobuy Spreadsheet updates</p>
              {submitted ? (
                <p className="text-accent-400 text-sm font-medium">Thanks for subscribing!</p>
              ) : (
                <form onSubmit={handleSubmit} className="flex gap-2" data-readdy-form="">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="flex-1 bg-transparent border border-foreground-700 text-white text-sm px-4 py-2.5 rounded-lg outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-600"
                  />
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-anti-spam-field"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Subscribe'}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </button>
                </form>
              )}
              {formError && (
                <p className="text-red-400 text-xs mt-1">{formError}</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-foreground-500 text-xs">
            KakobuyFinds is an independent resource. We are not affiliated with or endorsed by any brands mentioned.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-foreground-500 text-xs hover:text-foreground-300 transition-colors whitespace-nowrap">Privacy Policy</a>
            <a href="#" className="text-foreground-500 text-xs hover:text-foreground-300 transition-colors whitespace-nowrap">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}